import { doGet2 } from 'services/common'
import { adminRequest } from 'utils/adminRequest'

// inlined (not imported from PQResult) to avoid a circular import:
// PQResult -> ReferralCard(richtextField) -> PqHideResultsField -> here
const pqResultLabel = (props: { title?: string } | undefined, i: number) => {
  const text = (props?.title ?? '').replace(/<[^>]*>/g, '').trim()
  return text || `Result ${i + 1}`
}

export type PqResultRef = {
  stepId: number
  stepLabel: string
  resultId: string
  label: string
}

export type PqStepResults = {
  stepId: number
  stepLabel: string
  results: { resultId: string; label: string }[]
}

/**
 * Discover Personality Quiz results across all puck steps on this site, with
 * NO backend changes: the admin step-builder list endpoint returns every
 * step's puck JSON inline, so we parse it client-side, keep the ones with a
 * PQContainer, and pull out their results. The referral card's PQ hide-list
 * references PQResult component ids (stable, rename/reorder-proof).
 */
let cache: Promise<PqStepResults[]> | null = null

const walkFindPQContainer = (nodes: any[]): any => {
  for (const n of nodes ?? []) {
    if (n?.type === 'PQContainer') return n
    for (const v of Object.values(n?.props ?? {})) {
      if (Array.isArray(v)) {
        const hit = walkFindPQContainer(v as any[])
        if (hit) return hit
      }
    }
  }
  return undefined
}

const parseStep = (row: {
  id: number
  stepType?: string
  permalink?: string
  content?: string
}): PqStepResults | null => {
  if (row.stepType !== 'puck' || !row.content) return null
  let data: any
  try {
    data = JSON.parse(row.content)
  } catch {
    return null
  }
  const pq = walkFindPQContainer(data?.content ?? [])
  if (!pq) return null
  const results = ((pq.props?.results ?? []) as any[])
    .filter(r => r?.type === 'PQResult' && r.props?.id)
    .map((r, i) => ({
      resultId: String(r.props.id),
      label: pqResultLabel(r.props, i),
    }))
  if (!results.length) return null
  return {
    stepId: row.id,
    stepLabel:
      (pq.props?.headline as string) ||
      row.permalink ||
      `Step ${row.id}`,
    results,
  }
}

export const fetchPqResults = (force = false): Promise<PqStepResults[]> => {
  if (cache && !force) return cache
  const p: Promise<PqStepResults[]> = adminRequest(async () => {
    const res = await doGet2('/admin/step-builder?page=0&size=500', {})
    if (!res.ok) throw new Error(`step list failed (${res.status})`)
    const json = await res.json()
    const rows = (json?.content ?? []) as Parameters<typeof parseStep>[0][]
    return rows
      .map(parseStep)
      .filter((s): s is PqStepResults => !!s)
  }).catch(() => [] as PqStepResults[])
  cache = p
  return p
}
